import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import HeaderMenu from '../components/HeaderMenu';
import LogSummaryPopover from '../components/LogSummaryPopover';

const RANGE_PRESETS = [
  { key: '30', label: 'Last 30 days', days: 30 },
  { key: '90', label: 'Last 90 days', days: 90 },
  { key: '365', label: 'Last year', days: 365 },
  { key: 'all', label: 'All time', days: 3650 },
];

const isoDay = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// ---------------------------------------------------------------------------
// Sparkline — inline SVG bar chart. Renders a fixed-width row of vertical bars
// sized to the max value in the window. Zero-value days show as a subtle base
// line so a long silent stretch is visually obvious.
// ---------------------------------------------------------------------------
const Sparkline = ({ series, width = 220, height = 32, from, to }) => {
  const byDate = useMemo(() => {
    const m = new Map();
    series.forEach((p) => m.set(p.date, (m.get(p.date) || 0) + p.qty));
    return m;
  }, [series]);

  // Build day-by-day array between from/to so gaps render as empty bars.
  const days = useMemo(() => {
    if (!from || !to) return series;
    const out = [];
    const start = new Date(from);
    const end = new Date(to);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const iso = isoDay(d);
      out.push({ date: iso, qty: byDate.get(iso) || 0 });
    }
    return out;
  }, [from, to, series, byDate]);

  if (days.length === 0) {
    return <div className="text-xs text-slate-400 italic">no data</div>;
  }
  const max = Math.max(1, ...days.map((p) => p.qty));
  const barW = width / days.length;

  return (
    <svg width={width} height={height} className="block" data-testid="sparkline">
      <line x1={0} y1={height - 1} x2={width} y2={height - 1} stroke="#e2e8f0" />
      {days.map((p, i) => {
        const h = p.qty > 0 ? Math.max(1, (p.qty / max) * (height - 2)) : 0;
        return (
          <rect
            key={i}
            x={i * barW}
            y={height - h}
            width={Math.max(1, barW - 0.5)}
            height={h}
            fill={p.qty > 0 ? '#0f172a' : 'transparent'}
          >
            <title>{`${p.date}: ${p.qty}`}</title>
          </rect>
        );
      })}
    </svg>
  );
};

// ---------------------------------------------------------------------------
// Trackable part row — sparkline + total + expand-to-see-logs drilldown.
// ---------------------------------------------------------------------------
const TrackedPartRow = ({ part, from, to, onOpenLog }) => {
  const [expanded, setExpanded] = useState(false);
  const monthlyBars = useMemo(() => {
    const m = new Map();
    part.series.forEach((p) => {
      const month = p.date.slice(0, 7);
      m.set(month, (m.get(month) || 0) + p.qty);
    });
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [part.series]);
  const monthMax = Math.max(1, ...monthlyBars.map(([, v]) => v));

  return (
    <div className="border rounded-md" data-testid={`tracked-part-row-${part.part_number}`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-4 p-3 hover:bg-slate-50 text-left"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-semibold text-slate-900">{part.part_number}</span>
            <span className="text-sm text-slate-600 truncate">{part.name}</span>
            <span className="text-[10px] uppercase tracking-wide text-slate-400">
              {part.current_stock} {part.unit} in stock
            </span>
          </div>
        </div>
        <div className="hidden md:block">
          <Sparkline series={part.series} from={from} to={to} />
        </div>
        <div className="text-right shrink-0">
          <div className="text-2xl font-bold text-slate-900 leading-none" data-testid={`tracked-part-total-${part.part_number}`}>
            {part.total_qty}
          </div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wide">consumed</div>
        </div>
        <div className="text-slate-400 text-lg">{expanded ? '▾' : '▸'}</div>
      </button>

      {expanded && (
        <div className="border-t p-3 bg-slate-50/70 space-y-3">
          {/* Monthly bars */}
          {monthlyBars.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium mb-2">
                By month
              </div>
              <div className="flex items-end gap-2 h-24">
                {monthlyBars.map(([month, qty]) => {
                  const h = (qty / monthMax) * 80;
                  return (
                    <div key={month} className="flex flex-col items-center text-[10px]" style={{ minWidth: 26 }}>
                      <div className="text-slate-700 font-medium mb-0.5">{qty}</div>
                      <div
                        className="w-full bg-slate-900 rounded-sm"
                        style={{ height: `${Math.max(2, h)}px` }}
                        title={`${month}: ${qty}`}
                      />
                      <div className="text-slate-500 mt-1">{month.slice(5)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Underlying logs */}
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium mb-2">
              {part.logs.length} log{part.logs.length === 1 ? '' : 's'} contributed
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {part.logs.map((log) => (
                <button
                  key={log.id}
                  type="button"
                  onClick={() => onOpenLog(log)}
                  className="w-full text-left text-sm p-2 border rounded bg-white hover:bg-slate-100 flex items-center gap-3"
                  data-testid={`tracked-part-log-${log.id}`}
                >
                  <span className="text-xs text-slate-500 w-24 shrink-0">{log.date}</span>
                  <span className="font-medium truncate flex-1">{log.equipment_name}</span>
                  <span className="text-xs text-slate-600 truncate max-w-[45%]">
                    {log.work_description}
                  </span>
                  <Badge className="bg-slate-900 text-white text-xs shrink-0">×{log.qty}</Badge>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Hours per location — big bars + top-5 equipment.
// ---------------------------------------------------------------------------
const HoursByLocation = ({ data }) => {
  if (!data || data.by_location.length === 0) {
    return (
      <div className="text-sm text-slate-500 italic">
        No logged hours in this window.
      </div>
    );
  }
  const max = Math.max(1, ...data.by_location.map((l) => l.total_hours));
  return (
    <div className="space-y-2" data-testid="hours-by-location">
      {data.by_location.map((loc) => {
        const pct = (loc.total_hours / max) * 100;
        return (
          <div key={loc.location_id} className="flex items-center gap-3" data-testid={`hours-loc-${loc.location_id}`}>
            <div className="w-40 shrink-0 truncate text-sm font-medium">{loc.location_name}</div>
            <div className="flex-1 h-6 bg-slate-100 rounded overflow-hidden">
              <div
                className="h-full bg-slate-900 transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="w-32 text-right text-sm">
              <span className="font-semibold">{loc.total_hours.toFixed(1)} h</span>
              <span className="text-slate-500 text-xs ml-1">({loc.log_count} logs)</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
const Analytics = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [rangeKey, setRangeKey] = useState('365');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [partsData, setPartsData] = useState(null);
  const [hoursData, setHoursData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openLog, setOpenLog] = useState(null);

  const { from, to } = useMemo(() => {
    if (rangeKey === 'custom' && customFrom && customTo) {
      return { from: customFrom, to: customTo };
    }
    const preset = RANGE_PRESETS.find((p) => p.key === rangeKey) || RANGE_PRESETS[2];
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() - preset.days);
    return { from: isoDay(start), to: isoDay(today) };
  }, [rangeKey, customFrom, customTo]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [pRes, hRes] = await Promise.all([
          axios.get(`${API}/analytics/parts-consumption?from=${from}&to=${to}`),
          axios.get(`${API}/analytics/hours-worked?from=${from}&to=${to}`),
        ]);
        setPartsData(pRes.data);
        setHoursData(hRes.data);
      } catch (e) {
        toast.error('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    })();
  }, [from, to]);

  const handleOpenLog = async (logSummary) => {
    // The parts-consumption endpoint gave us a partial log object; fetch full
    // detail so images + notes render in the popover.
    try {
      const res = await axios.get(`${API}/maintenance-logs/${logSummary.id}`);
      setOpenLog({
        raw_id: logSummary.id,
        event_type: 'maintenance_log',
        title: (res.data.work_description || 'Maintenance log').split('\n', 1)[0].slice(0, 120),
        equipment_name: logSummary.equipment_name,
        date: logSummary.date,
        description: res.data.work_description,
        resolution: res.data.resolution,
        parts_used: res.data.parts_used,
        notes: res.data.notes,
        hours_spent: res.data.hours_spent,
        actor_name: res.data.technician_name,
        machinery_id: logSummary.machinery_id,
        images: res.data.images || [],
      });
    } catch {
      setOpenLog({
        raw_id: logSummary.id,
        event_type: 'maintenance_log',
        title: logSummary.work_description || 'Maintenance log',
        equipment_name: logSummary.equipment_name,
        date: logSummary.date,
        description: logSummary.work_description,
        resolution: logSummary.resolution,
        parts_used: logSummary.parts_used,
        hours_spent: logSummary.hours_spent,
        machinery_id: logSummary.machinery_id,
      });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Analytics</h1>
            <p className="text-sm text-slate-600 mt-1">
              Parts consumption and hours-worked trends
            </p>
          </div>
          <HeaderMenu user={user} onLogout={onLogout} />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Range picker */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-3">
              {RANGE_PRESETS.map((p) => (
                <Button
                  key={p.key}
                  variant={rangeKey === p.key ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setRangeKey(p.key)}
                  data-testid={`range-${p.key}-btn`}
                >
                  {p.label}
                </Button>
              ))}
              <Button
                variant={rangeKey === 'custom' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setRangeKey('custom')}
                data-testid="range-custom-btn"
              >
                Custom
              </Button>
              {rangeKey === 'custom' && (
                <>
                  <Input
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    className="w-40"
                    data-testid="range-custom-from"
                  />
                  <span className="text-slate-500">→</span>
                  <Input
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    className="w-40"
                    data-testid="range-custom-to"
                  />
                </>
              )}
              <div className="ml-auto text-xs text-slate-500">
                {from} → {to}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Parts Consumption */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle>Parts Consumed</CardTitle>
              <div className="text-xs text-slate-500">
                Only parts marked with ★ on the{' '}
                <button
                  type="button"
                  className="underline hover:text-slate-800"
                  onClick={() => navigate('/inventory')}
                >
                  Inventory page
                </button>{' '}
                are shown.
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading && <div className="text-sm text-slate-500">Loading…</div>}
            {!loading && partsData && partsData.tracked_parts.length === 0 && (
              <div className="text-sm text-slate-500 italic border rounded-md p-4">
                No parts are being tracked yet. Head to Inventory, click the ★ next to any part number to start trending its consumption here.
              </div>
            )}
            {!loading && partsData && partsData.tracked_parts.length > 0 && (
              <div className="space-y-2">
                {partsData.tracked_parts.map((p) => (
                  <TrackedPartRow
                    key={p.part_number}
                    part={p}
                    from={from}
                    to={to}
                    onOpenLog={handleOpenLog}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Hours worked */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Hours Worked · by Location</CardTitle>
            </CardHeader>
            <CardContent>
              {loading && <div className="text-sm text-slate-500">Loading…</div>}
              {!loading && hoursData && <HoursByLocation data={hoursData} />}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Top Equipment by Hours</CardTitle>
            </CardHeader>
            <CardContent>
              {loading && <div className="text-sm text-slate-500">Loading…</div>}
              {!loading && hoursData && hoursData.top_equipment.length === 0 && (
                <div className="text-sm text-slate-500 italic">No hours logged in this window.</div>
              )}
              {!loading && hoursData && hoursData.top_equipment.length > 0 && (
                <ol className="space-y-2" data-testid="top-equipment-list">
                  {hoursData.top_equipment.map((eq, i) => (
                    <li
                      key={eq.machinery_id}
                      className="flex items-center gap-3"
                      data-testid={`top-eq-${i}`}
                    >
                      <span className="text-xs font-mono text-slate-400 w-4">{i + 1}</span>
                      <button
                        type="button"
                        onClick={() => navigate(`/machinery/${eq.machinery_id}`)}
                        className="flex-1 text-left hover:underline text-sm truncate"
                      >
                        <div className="font-medium truncate">{eq.equipment_name}</div>
                        {eq.location_name && (
                          <div className="text-xs text-slate-500 truncate">{eq.location_name}</div>
                        )}
                      </button>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-semibold">{eq.total_hours.toFixed(1)} h</div>
                        <div className="text-[10px] text-slate-500">{eq.log_count} logs</div>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <LogSummaryPopover
        open={!!openLog}
        onClose={() => setOpenLog(null)}
        event={openLog}
      />
    </div>
  );
};

export default Analytics;
